import { computed, observable, action } from 'mobx'
import { type Team } from '@/features/iam/teams/Team'
import { TeamItemViewModel } from '@/features/iam/teams/TeamItemViewModel'
import { orderBy } from 'lodash-es'
import type { ActivationLifecycle } from '@/infra/activation'
import { LifecycleTracker } from '@/infra/lifecycle-tracker'
import { ViewModelList } from '@/infra/view-model-list'

export class TeamListViewModel implements ActivationLifecycle {
  private readonly _lifecycle = new LifecycleTracker()

  private readonly _teamItems = new ViewModelList({
    models: () => this.filtered,
    create: (team: Team) => new TeamItemViewModel(team),
  })

  @observable accessor searchText = ''

  constructor(private readonly _getTeams: () => Team[]) {
    this._lifecycle.manage(this._teamItems)
  }

  activate() {
    this._lifecycle.activate()
  }

  deactivate() {
    this._lifecycle.deactivate()
  }

  /**
   * Teams the current user is a member of.
   */
  @computed
  get teams() {
    return this._getTeams()
  }

  @computed
  get teamItems() {
    return this._teamItems.items
  }

  @computed
  get filtered() {
    if (this.searchText === '') {
      return orderBy(this.teams, 'name')
    }

    return orderBy(
      this.teams.filter(
        (team) =>
          team.name.toLowerCase().includes(this.searchText.toLowerCase()) ||
          team.owner.member.displayName
            .toLowerCase()
            .includes(this.searchText.toLowerCase()),
      ),
      'name',
    )
  }

  @action.bound
  setSearchText(val: string) {
    this.searchText = val
  }
}
